// MedMalGuard "Claude1" landing page (Concept 1 — Calculator-first).
//
// Recreates the approved PA quote-to-bind landing design from the
// design_handoff bundle, built with this app's inline-style approach (no
// Tailwind) and the kit's tokens. It is a self-contained, responsive
// landing rendered ONLY on the "/" route (see FlowLayout): a new nav, a
// two-column hero whose right side is a live quote calculator, a trust
// band, how-it-works, a need-help card, and the new dark footer.
//
// Wiring: the calculator reuses the EXISTING soft-quote flow — it writes
// ZIP + effective date into quoteFormStore and runs the same fetchIlfDlf →
// navigate(STEP_PATHS[step]) handoff LocationDatePage uses, so every CTA
// hands off to the current quote→bind flow. No new routes are invented.
//
// Colors come from the kit. Where the active theme is medmalguard ("Cl1")
// the runtime --brand* tokens already resolve to the kit blue, but this
// page hard-codes the kit palette so it renders faithfully under any theme
// (it is the approved, fixed design — not theme-driven chrome).

import { useNavigate } from "react-router-dom";
import { useStore } from "@/shared/store/useStore.js";
import quoteFormStore from "@/modules/Quote/store/quoteFormStore.js";
import signupStore from "@/modules/Auth/store/signupStore.js";
import loginStore from "@/modules/Auth/store/loginStore.js";
import submissionStore from "@/modules/Quote/store/submissionStore.js";
import insuredProfileStore from "@/shared/store/insuredProfileStore.js";
import modalStore from "@/shared/store/modalStore.js";
import { STEP_PATHS } from "@/modules/Quote/constants.js";
import { isValidZip } from "@/shared/utils/validators.js";
import { dateValid, fmtDate, formatDate, toMdY } from "@/shared/utils/dateHelpers.js";
import { ilfDlfHasRequiredDefaults, softQuotePremiumFromIlf, getDefaultIlfDlfRow, formatIlfLimitShort, policyTypeFromIlf, expirationMdYFromEffAndIlf } from "@/modules/Quote/utils/ilfHelpers.js";
import { useState, useEffect, useRef } from "react";
import { ZIPCODE_NOT_AVAILABLE_MSG, toError } from "@/shared/utils/misc.js";
import ilfDlfStore from "@/modules/Quote/store/ilfDlfStore.js";
import { useSpecialityId } from "@/hooks/useSpeciality.js";
import {
  resetIlfDlfError,
  resetIlfDlfResponse,
  useIlfDlfError,
  useIlfDlfFetcher,
  useIlfDlfLoading,
} from "@/hooks/useIlfDlfFetcher.js";
import {
  Shield, Check, CheckCircle, Phone, Mail, FileText, ArrowRight, Star, Calendar,
  ChevronDown, X,
} from "@/shared/components/desktop/HubIcons.jsx";
import { signOut } from "@/modules/Auth/services/authSessionService.js";
import { TESTIMONIALS } from "@/shared/components/desktop/hubData.js";

// ── Kit tokens (design_handoff/ui-kit/medmalguard-ui.css) ────────────────
const C = {
  accent: "#6286ed", accentStrong: "#2653d4", accentTint: "#eff3fd",
  ink: "#2a2c45", inkSoft: "#3a3b50", body: "#6e7080", muted: "#9598a8",
  bg: "#ffffff", bgSubtle: "#f8f9fc", bgApp: "#fafbfd", dark: "#2a2c45",
  onDarkMuted: "#aab0c8",
  border: "#e3e6f0", borderSoft: "#eef0f5", inputBorder: "#d7dbe8",
  success: "#1cc88a", successStrong: "#0f9d6e", successTint: "#e6f8f1",
  successBorder: "#c9efdf", successDeep: "#0a6b4a",
};
const FONT = "'Inter', ui-sans-serif, system-ui, -apple-system, sans-serif";
const FOCUS_RING = "0 0 0 0.1rem rgba(78,115,223,0.25)";

const PHONE = "(888) 966-3881";
const PHONE_HREF = "tel:+18889663881";
const SUPPORT_EMAIL = "support@selectfirstinsurance.com";

export default function MedMalGuardLanding() {
  const navigate = useNavigate();

  const zip     = useStore(quoteFormStore, (s) => s.zip);
  const effDate = useStore(quoteFormStore, (s) => s.effDate);
  const regEmail = useStore(signupStore, (s) => s.regEmail);
  const insuredProfile  = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);

  const specialityId  = useSpecialityId();
  const fetchIlfDlf   = useIlfDlfFetcher();
  const ilfDlfError   = useIlfDlfError();
  const ilfDlfLoading = useIlfDlfLoading();
  // The estimate display is derived from the DURABLE ILF/DLF cache (keyed by
  // zip|speciality|effectiveDate) rather than local state, so an estimate the
  // user already fetched still shows after they Continue-to-bind and navigate
  // back: this component remounts, but the store slot survives. Editing a
  // field changes the key, so a stale price auto-hides until it's refetched.
  const fieldsValid = isValidZip(zip) && dateValid(effDate);
  const currentEstimateKey = ilfDlfStore.cacheKey({
    zipcode: zip, specialtiesid: specialityId, effectiveDate: fmtDate(effDate),
  });
  const cachedEstimate = useStore(ilfDlfStore, (s) => s.getIlfDlf(currentEstimateKey));

  const hasEstimate =
    fieldsValid && Boolean(cachedEstimate) && ilfDlfHasRequiredDefaults(cachedEstimate);

  // Derive the displayed numbers from the cached estimate for the current inputs.
  // `premium` is the base premium before fees/taxes; `total` (via
  // softQuotePremiumFromIlf) is the all-in figure shown as "total due".
  const annualPremium = hasEstimate && Number.isFinite(Number(cachedEstimate?.premium))
    ? Math.round(Number(cachedEstimate.premium))
    : null;
  const defaultRow = hasEstimate ? getDefaultIlfDlfRow(cachedEstimate) : null;
  const limitsLabel = defaultRow ? formatIlfLimitShort(defaultRow) : "";
  const policyType = defaultRow ? policyTypeFromIlf(defaultRow) : "";

  // Coverage-snapshot fields, derived from the same cached estimate.
  const snapState = hasEstimate
    ? String(cachedEstimate?.st ?? cachedEstimate?.state ?? "").trim() || "Your state"
    : "";
  const snapExpiry = hasEstimate ? expirationMdYFromEffAndIlf(fmtDate(effDate), cachedEstimate) : "";

  // Fee/tax line items come straight from the quotedata response — labels are
  // carrier- and state-driven, so they are rendered as returned rather than
  // mapped onto fixed rows.
  const feeRows = hasEstimate && Array.isArray(cachedEstimate?.fees) ? cachedEstimate.fees : [];
  const taxRows = hasEstimate && Array.isArray(cachedEstimate?.taxes) ? cachedEstimate.taxes : [];

  // `total` already includes premium + fees + taxes, so it is the total due.
  const totalDue = hasEstimate ? softQuotePremiumFromIlf(cachedEstimate) : null;

  // Fetch the estimate WITHOUT navigating — used both on auto-trigger (valid
  // date entered) and the explicit "Get estimate" affordance.
  const getEstimate = async () => {
    if (!fieldsValid || ilfDlfLoading) return;
    resetIlfDlfError();
    try {
      const data = await fetchIlfDlf({
        zipcode: zip,
        specialtiesid: specialityId,
        effectiveDate: fmtDate(effDate),
      });
      // On success `fetchIlfDlf` stores the response in ilfDlfStore under the
      // current key, so `cachedEstimate`/`hasEstimate` update reactively — no
      // local flag to set. Only a "no coverage for this zip" result needs a
      // hand-set error (and the cached response cleared).
      if (!ilfDlfHasRequiredDefaults(data)) {
        resetIlfDlfResponse();
        ilfDlfStore.ilfDlfError = toError(new Error(ZIPCODE_NOT_AVAILABLE_MSG));
      }
    } catch (_) {
      // fetchIlfDlf already captured the network/HTTP error.
    }
  };

  // Continue to bind — only valid once an estimate is showing. Advances into
  // the existing soft-quote step of the wizard (no new routes).
  const handleContinue = () => {
    if (!hasEstimate) return;
    submissionStore.step = 1;
    navigate(STEP_PATHS[1]); // /classification
  };

  // Entering a valid effective date (Enter or leaving the field) fetches the
  // estimate inline so the price shows on this page.
  const onEffDateKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    e.currentTarget.blur();
    getEstimate();
  };
  const onEffDateBlur = () => { if (fieldsValid && !cachedEstimate) getEstimate(); };

  const onSignIn = () => {
    if (isAuthenticated) { navigate("/dashboard"); return; }
    if (regEmail.trim()) loginStore.loginEmail = regEmail.trim();
    navigate("/signin");
  };

  // Header "Get a quote" on the landing: the calculator IS the hero, so
  // reset the form (ZIP + effective date) and any stale estimate so it starts
  // blank, then smooth-scroll to it and focus the ZIP field.
  const onGetQuote = () => {
    quoteFormStore.zip = "";
    quoteFormStore.effDate = "";
    resetIlfDlfError();
    const el = document.getElementById("quote");
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    const zipInput = el.querySelector("input");
    if (zipInput) setTimeout(() => zipInput.focus({ preventScroll: true }), 400);
  };

  const msg = ilfDlfError?.message || "";
  const showError = Boolean(ilfDlfError);

  return (
    <div className="mmg-landing" style={{ fontFamily: FONT, color: C.body, background: C.bg }}>
      <NavBar onSignIn={onSignIn} onGetQuote={onGetQuote} isAuthenticated={isAuthenticated} />

      {/* ── Hero ───────────────────────────────────────────────────────── */}
      <div className="mmg-section mmg-hero">
        <div className="mmg-hero__left">
          <span className="mmg-chip" style={chip}>
            <Check size={13} color={C.accentStrong} strokeWidth={3} />
            Coverage by Doctors Professional Liability, RRG
          </span>

          <h1 className="mmg-h1" style={h1}>Physician Assistant <br className="mmg-h1__br" />Malpractice Insurance</h1>

          <p className="mmg-sub" style={sub}>
            See a real, carrier-backed price in minutes — then bind your
            coverage online in a single session. No agent call required.
          </p>

          {/* Certificate-in-minutes callout */}
          <div style={callout}>
            <FileText size={22} color={C.successStrong} />
            <div>
              <div style={{ fontWeight: 800, fontSize: 18, color: C.successDeep, lineHeight: 1.2 }}>
                Certificate in minutes
              </div>
              <div style={{ fontWeight: 500, fontSize: 13, color: C.successStrong, marginTop: 3 }}>
                On straightforward submissions — emailed instantly
              </div>
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 13, marginTop: 22 }}>
            {[
              "Bind online in one session — no agent call required",
              "10,000+ practitioners insured and counting",
              "License Defense Protection included",
              "AAPA Member Discounts",
            ].map((t) => (
              <div key={t} style={{ display: "flex", alignItems: "center", gap: 11, fontWeight: 500, fontSize: 15, color: C.inkSoft }}>
                <CheckCircle size={20} color={C.success} />
                <span>{t}</span>
              </div>
            ))}
          </div>

          {/* Star rating + social-proof line, shown above the Demotech seal. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 26 }}>
            <span style={{ display: "inline-flex", gap: 2 }}>
              {[0, 1, 2, 3, 4].map((i) => (
                <Star key={i} size={18} color="#f6c23e" fill="#f6c23e" />
              ))}
            </span>
            <span style={{ fontWeight: 500, fontSize: 15, color: C.body }}>
              Trusted by <strong style={{ fontWeight: 800, color: C.ink }}>10,000+</strong> insured practitioners
            </span>
          </div>

          <img
            src="/demotech-fsr-a.png"
            alt="Demotech Financial Stability Rating: A (Exceptional)"
            style={{ height: 60, width: "auto", marginTop: 14, display: "block" }}
          />
        </div>

        {/* Quote calculator card. id="quote" is the scroll target for the
            header "Get a quote" CTA; scroll-margin-top clears the sticky nav. */}
        <div className="mmg-hero__right" id="quote" style={{ scrollMarginTop: 90 }}>
          <div style={calcCard}>
            <div style={calcHeader}>
              <span style={{ fontWeight: 700, fontSize: 14, color: C.ink }}>Instant estimate</span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, background: C.successTint, color: C.successStrong, fontWeight: 700, fontSize: 11, padding: "5px 10px", borderRadius: 20 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.success }} />
                Live carrier price
              </span>
            </div>

            <div style={{ padding: "24px 22px" }}>
              <CalcField label="ZIP code">
                <CalcInput
                  value={zip}
                  onChange={(v) => { quoteFormStore.zip = v.replace(/\D/g, "").slice(0, 5); }}
                  placeholder="e.g. 92688"
                  inputMode="numeric"
                  maxLength={5}
                />
              </CalcField>

              <CalcField label="Effective date">
                {/* No date-range restriction on the picker — any date may be
                    selected. The estimate button only requires a filled,
                    valid-format date (dateValid). */}
                <CalcDateInput
                  value={effDate}
                  onChange={(v) => { quoteFormStore.effDate = formatDate(v); }}
                  onKeyDown={onEffDateKeyDown}
                  onBlur={onEffDateBlur}
                  placeholder="MM/DD/YYYY"
                />
              </CalcField>

              {/* Estimate block — shows the live carrier price once a valid
                  ZIP + effective date have produced an estimate; otherwise a
                  prompt / "Get estimate" affordance. */}
              <div style={estimateBlock}>
                <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: C.body }}>
                  Estimated premium
                </div>

                {hasEstimate ? (
                  <>
                    <div style={{ fontSize: 13, color: C.muted, marginTop: 3 }}>
                      {[limitsLabel, policyType].filter(Boolean).join(" · ") || "Standard limits"}
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 8 }}>
                      <span style={{ fontWeight: 800, fontSize: 38, color: C.ink, lineHeight: 1 }}>
                        ${totalDue?.toLocaleString()}
                      </span>
                      <span style={{ fontWeight: 600, fontSize: 16, color: C.body }}>/ yr</span>
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
                      Includes all fees and taxes
                    </div>

                    {/* Expanded breakdown — appears inline within the same
                        estimate block; the user stays on this page. */}
                    <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 16, paddingTop: 14 }}>
                      <div style={{ fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".05em", color: C.body, marginBottom: 10 }}>
                      Your price, all-in
                      </div>
                      <EstimateRow label="Annual premium" value={annualPremium} />
                      {feeRows.map((f, i) => (
                        <EstimateRow key={`fee-${i}`} label={f?.label || "Fee"} value={Math.round(Number(f?.value) || 0)} />
                      ))}
                      {/* Taxes display as one combined row (front-end sum
                          of the carrier's tax line items). */}
                      {taxRows.length > 0 && (
                        <EstimateRow label="Taxes" value={Math.round(taxRows.reduce((sum, t) => sum + (Number(t?.value) || 0), 0))} />
                      )}
                      <div style={{ borderTop: `1px solid ${C.border}`, margin: "8px 0" }} />
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "4px 0" }}>
                        <span style={{ fontWeight: 800, fontSize: 14, color: C.ink }}>Total due today</span>
                        <span style={{ fontWeight: 800, fontSize: 14, color: C.ink }}>${totalDue?.toLocaleString()}</span>
                      </div>
                      <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.5, marginTop: 8 }}>
                        Your capital contribution makes you a member-owner of the risk retention group.
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 14, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                      {ilfDlfLoading
                        ? "Calculating your estimate…"
                        : "Enter your ZIP and effective date to see your price."}
                    </div>
                    {fieldsValid && !ilfDlfLoading && (
                      <button type="button" onClick={getEstimate}
                        style={{ marginTop: 12, background: "none", border: `1px solid ${C.accent}`, color: C.accentStrong, fontFamily: "inherit", fontWeight: 700, fontSize: 13, padding: "9px 14px", borderRadius: 8, cursor: "pointer" }}>
                        Get estimate
                      </button>
                    )}
                  </>
                )}
              </div>

              {/* Detail sections — shown once an estimate exists, below the
                  breakdown and above "Continue to bind". The user stays on
                  this page. */}
              {hasEstimate && (
                <>
                  <InfoNote tone="info">
                    Estimate based on standard PA 1 rates. Complete the full application for your exact quote — the total above already includes all taxes and fees. No surprises at checkout.
                  </InfoNote>

                  <div style={snapCard}>
                    <div style={snapTitle}>Coverage snapshot</div>
                    <SnapRow label="State · limits" value={[snapState, limitsLabel].filter(Boolean).join(" · ") || "—"} />
                    <SnapRow label="Policy period" value={[fmtDate(effDate), snapExpiry].filter(Boolean).join(" – ") || "—"} />
                    <SnapRow label="Form" value={[policyType, "inclusive of limits"].filter(Boolean).join(" · ")} />
                  </div>

                  <div style={snapCard}>
                    <div style={snapTitle}>Risk Retention Group notice</div>
                    <div style={{ fontSize: 13, color: C.body, lineHeight: 1.6 }}>
                      This policy is issued by your risk retention group. Your risk retention group may not be subject to all of the insurance laws and regulations of your state. State insurance insolvency guaranty funds are not available for your risk retention group.
                    </div>
                  </div>
                </>
              )}

              {showError && (
                <div style={{ background: "#fdf4f3", border: "1px solid #f5dcd9", color: "#c0564f", borderRadius: 10, padding: "10px 12px", fontSize: 13, fontWeight: 500, marginBottom: 12 }}>
                  {msg.toLowerCase().includes("zip")
                    ? ZIPCODE_NOT_AVAILABLE_MSG
                    : (msg || "Could not load coverage options for this zip. Please try again.")}
                </div>
              )}

              <button type="button" onClick={handleContinue} style={ctaBtn(hasEstimate)}
                onMouseEnter={(e) => { if (hasEstimate) e.currentTarget.style.background = C.accentStrong; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = C.accent; }}>
                Continue to bind <ArrowRight size={17} color="#fff" />
              </button>

              {!isAuthenticated && (
                <div style={{ fontWeight: 500, fontSize: 13, color: C.muted, textAlign: "center", marginTop: 12 }}>
                  Already have an account?{" "}
                  <button type="button" onClick={onSignIn}
                    style={{ background: "none", border: 0, padding: 0, font: "inherit", fontWeight: 600, color: C.accentStrong, textDecoration: "underline", cursor: "pointer" }}>
                    Sign in
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Trust band ─────────────────────────────────────────────────── */}
      <div className="mmg-trust-band">
        <TrustCell eyebrow="Coverage provided by" value="DPL RRG" caption="Doctors Professional Liability, RRG" />
        <TrustCell eyebrow="Financial stability" value="Demotech" caption="Rated A (Exceptional)" />
        <TrustCell eyebrow="Reinsured by" value="Lloyd's" caption="of London" />
        <TrustCell eyebrow="Track record" value="10,000+" caption="Practitioners insured" />
      </div>

      {/* ── How it works ───────────────────────────────────────────────── */}
      <div className="mmg-section">
        <span style={{ fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em", color: C.muted }}>How it works</span>
        <h2 style={{ fontWeight: 800, fontSize: 30, letterSpacing: "-.01em", color: C.ink, margin: "10px 0 26px" }}>
          From price to certificate in three steps
        </h2>
        <div className="mmg-how-grid">
          <HowCard n="1" title="See your price" body="Enter your ZIP and effective date for a live, carrier-backed estimate — no SSN or credit check." accent />
          <HowCard n="2" title="Confirm & bind" body="Answer a short set of questions, choose your limits, and bind your policy online in one session." accent />
          <HowCard n="3" title="Get your certificate" body="Your certificate of insurance is emailed instantly on straightforward submissions." />
        </div>
      </div>

      {/* ── Reviews (mobile / tablet) ──────────────────────────────────── */}
      {/* Two testimonials for < 1024px viewports, where the full HubMarketing
          testimonial band is hidden (.hub-desktop). Desktop hides THIS section
          instead (.mmg-reviews in responsive.css) so reviews never render
          twice on one page. Content reuses the canonical TESTIMONIALS data. */}
      <div className="mmg-section mmg-reviews">
        <span style={{ fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em", color: C.muted }}>Reviews</span>
        <h2 style={{ fontWeight: 800, fontSize: 30, letterSpacing: "-.01em", color: C.ink, margin: "10px 0 26px" }}>
          What clinicians say
        </h2>
        <div className="mmg-reviews-grid">
          {TESTIMONIALS.slice(0, 2).map((t) => (
            <div key={t.id} style={reviewCard}>
              <span style={{ display: "inline-flex", gap: 2 }} aria-label={`${t.stars} out of 5 stars`}>
                {Array.from({ length: t.stars }).map((_, i) => (
                  <Star key={i} size={15} color="#f6c23e" fill="#f6c23e" />
                ))}
              </span>
              <p style={{ fontSize: 14, color: C.body, lineHeight: 1.65, margin: "10px 0 14px" }}>
                “{t.text}”
              </p>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <img src={t.avatar} alt="" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>{t.name}</div>
                  <div style={{ fontSize: 12, color: C.muted }}>{[t.role, t.location].filter(Boolean).join(" · ")}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Need help? ─────────────────────────────────────────────────── */}
      {/* Last section before the retained marketing sections (COI previewer
          adds its own 48px top padding + hairline), so trim the bottom
          padding here to avoid a doubled vertical gap. */}
      <div className="mmg-section" style={{ paddingTop: 0, paddingBottom: 8 }}>
        <div style={needCard}>
          <span style={{ fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: ".07em", color: C.muted }}>Need help?</span>
          <div className="mmg-need-rows">
            <a href={PHONE_HREF} style={needRow}>
              <span style={needIconWrap}><Phone size={18} color={C.accentStrong} /></span>
              <span><span style={needRowLabel}>Call</span><span style={needRowValue}>{PHONE}</span></span>
            </a>
            <a href={`mailto:${SUPPORT_EMAIL}`} style={needRow}>
              <span style={needIconWrap}><Mail size={18} color={C.accentStrong} /></span>
              <span><span style={needRowLabel}>Email</span><span style={needRowValue}>{SUPPORT_EMAIL}</span></span>
            </a>
          </div>
        </div>
      </div>

      {/* The new dark footer is rendered separately at the very END of the
          landing route (see MedMalGuardFooter, mounted last by FlowLayout)
          so it sits below all the retained previous sections. */}
    </div>
  );
}

// Landing footer, mounted last on the landing route by FlowLayout. Only the
// three contact affordances — phone, email, and the About modal — per the
// footer spec. Hidden on desktop (≥1024px, see .mmg-landing-footer in
// responsive.css) where HubMarketing's dark HubFooter closes the page.
export function MedMalGuardFooter() {
  return (
    <footer
      className="mmg-landing-footer"
      style={{
        fontFamily: FONT, background: C.bg, borderTop: `1px solid ${C.borderSoft}`,
        padding: "18px 16px", display: "flex", flexWrap: "wrap", alignItems: "center",
        justifyContent: "center", columnGap: 8, rowGap: 4, fontSize: 13, color: C.muted,
      }}
    >
      <a href={PHONE_HREF} style={{ color: C.accentStrong, textDecoration: "none", fontWeight: 600 }}>{PHONE}</a>
      <span aria-hidden="true">·</span>
      <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: C.accentStrong, textDecoration: "none", fontWeight: 600 }}>{SUPPORT_EMAIL}</a>
      <span aria-hidden="true">·</span>
      <button
        type="button"
        onClick={() => { modalStore.showAbout = true; }}
        style={{ background: "none", border: 0, padding: 0, font: "inherit", color: C.muted, textDecoration: "underline", cursor: "pointer" }}
      >
        About us
      </button>
    </footer>
  );
}

// "Affordable Coverage / Certificate in Minutes" map section, recreated
// from the live coverxpro SingleSpeciality landing page (LandingModule):
// two stacked headings split by a centered hairline, the US services map,
// an "Available in 20 States" bar, and the full grid of state codes in
// alternating tints. Mounted by FlowLayout AFTER the dark footer, per the
// requested placement (last section on the landing route).
//
// State codes + their alternating tint class are reproduced verbatim from
// the source (which itself contains the data quirks "GI" and "IS").
// The 20 states where coverage is actively written, in alphabetical order.
// Only these are shown in the grid (states we don't write in are omitted).
const MAP_STATES = [
  "AZ", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "IL", "MD",
  "MI", "NC", "NJ", "NY", "OH", "PA", "SC", "TN", "TX", "WA",
];

export function MedMalGuardMap() {
  return (
    <section className="mmg-map" style={{ fontFamily: FONT, background: "#fff" }}>
      {/* Heading block: "Affordable Coverage" / divider / "Certificate in Minutes" */}
      <div className="mmg-map__banner" style={{ background: "#e3f5ff", padding: "32px 24px", display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }}>
        <h2 style={{ margin: 0, fontWeight: 800, fontSize: 30, letterSpacing: "-.01em", color: C.ink, textAlign: "center" }}>Affordable Coverage</h2>
        <div style={{ width: "min(360px, 60%)", borderBottom: `5px solid ${C.ink}` }} />
        <h2 style={{ margin: 0, fontWeight: 800, fontSize: 30, letterSpacing: "-.01em", color: C.ink, textAlign: "center" }}>Certificate in Minutes</h2>
      </div>

      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "32px 40px" }}>
        {/* US services map */}
        <div style={{ textAlign: "center" }}>
          <img src="/us-services-map.png" alt="States where coverage is available" style={{ maxWidth: "100%", height: "auto", border: `1px solid ${C.border}`, borderRadius: 12, padding: 8, background: "#fff" }} />
        </div>

        {/* Available-in bar */}
        <div style={{ background: C.accentStrong, color: "#fff", borderRadius: 12, textAlign: "center", padding: "14px 16px", margin: "24px 0 20px" }}>
          <span style={{ fontWeight: 700, fontSize: 18 }}>Available in 20 States</span>
        </div>

        {/* State grid — the 20 states we actively write in, alphabetical. */}
        <div className="mmg-map__grid">
          {MAP_STATES.map((code) => (
            <div
              key={code}
              className="mmg-map__cell"
              style={{ background: "#f2f4f8", color: "#1a1a1a", fontWeight: 700 }}
            >
              {code}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────

function NavBar({ onSignIn, onGetQuote, isAuthenticated }) {
  const navigate = useNavigate();
  // Scroll-triggered header definition: the nav sits flush (no border/shadow)
  // at the very top, and once the page scrolls it gains a hairline + soft drop
  // shadow so the eye registers where the nav ends and content begins. Sticky
  // so it stays visible while scrolling (the pattern only reads when pinned).
  const [scrolled, setScrolled] = useState(false);
  // Mobile slide-out menu + logged-in avatar dropdown.
  const [menuOpen, setMenuOpen] = useState(false);
  const [acctOpen, setAcctOpen] = useState(false);
  const acctRef = useRef(null);
  const insuredProfile = useStore(insuredProfileStore, (s) => s.insuredProfile);
  // Same derivation the wizard header avatar uses (derivedValues.js).
  const initials = (insuredProfile?.abbreviation || insuredProfile?.username || "JD")
    .toString().slice(0, 2).toUpperCase();
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    onScroll(); // initialize for non-zero initial scroll positions
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Avatar dropdown dismissal: outside click or Escape.
  useEffect(() => {
    if (!acctOpen) return;
    const onDown = (e) => { if (acctRef.current && !acctRef.current.contains(e.target)) setAcctOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setAcctOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => { document.removeEventListener("mousedown", onDown); document.removeEventListener("keydown", onKey); };
  }, [acctOpen]);

  // Page scroll locks while the mobile menu sheet is open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [menuOpen]);

  const closeAll = () => { setMenuOpen(false); setAcctOpen(false); };
  const go = (fn) => { closeAll(); fn(); };
  const handleSignOut = async () => { closeAll(); await signOut(); navigate("/"); };

  const navLinkBtn = { background: "none", border: 0, padding: 0, fontFamily: "inherit", fontWeight: 600, fontSize: 13, color: C.body, textDecoration: "none", cursor: "pointer" };
  const avatarCircle = { width: 40, height: 40, borderRadius: "50%", background: "#dbe5fa", color: C.accent, fontWeight: 800, fontSize: 15, letterSpacing: ".02em", display: "inline-flex", alignItems: "center", justifyContent: "center" };
  const dropItem = { display: "block", width: "100%", textAlign: "left", background: "none", border: 0, padding: "10px 16px", fontFamily: "inherit", fontWeight: 600, fontSize: 13, color: C.inkSoft, cursor: "pointer" };
  const sheetRow = { display: "flex", alignItems: "center", width: "100%", textAlign: "left", background: "none", border: 0, borderTop: `1px solid ${C.borderSoft}`, padding: "20px 24px", fontFamily: "inherit", fontWeight: 700, fontSize: 19, color: C.ink, cursor: "pointer", textDecoration: "none" };

  // Menu-sheet rows (mobile). Logged in mirrors the approved mock: Dashboard,
  // Profile Settings, About Us, FAQ, … ; logged out leads with Home + Sign In.
  const sheetItems = [
    ...(isAuthenticated
      ? [
          { label: "Dashboard", onClick: () => navigate("/dashboard") },
          { label: "Profile Settings", onClick: () => navigate("/profile") },
        ]
      : [{ label: "Home", onClick: () => navigate("/") }]),
    { label: "About Us", onClick: () => { modalStore.showAbout = true; } },
    { label: "FAQ", onClick: () => { modalStore.showFAQ = true; } },
    { label: "Articles", onClick: () => navigate("/articles") },
    ...(isAuthenticated
      ? [{ label: "Sign out", onClick: handleSignOut, raw: true }]
      : [{ label: "Sign In", onClick: onSignIn }]),
  ];

  return (
    <nav style={{ ...nav, ...(scrolled ? navScrolled : null) }}>
      <div className="mmg-nav__inner" style={navInner}>
        {/* Brand lockup: shield + wordmark with the "by Select First
            Insurance" attribution line beneath (per the approved mock). */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ display: "inline-flex", width: 34, height: 34, alignItems: "center", justifyContent: "center", borderRadius: 8, background: C.accent, flexShrink: 0 }}>
            <Shield size={20} color="#fff" />
          </span>
          <span style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 800, fontSize: 19, color: C.ink, letterSpacing: "-.01em", lineHeight: 1.15 }}>
              MedMal<span style={{ color: C.accent }}>Guard</span>
            </span>
            <span style={{ fontWeight: 500, fontSize: 12, color: C.body, lineHeight: 1.25 }}>by Select First Insurance</span>
          </span>
        </div>

        <div className="mmg-nav__right" style={{ display: "flex", alignItems: "center", gap: 26 }}>
          <button type="button" onClick={() => go(() => navigate("/"))} style={navLinkBtn} className="mmg-nav__link">Home</button>
          <button type="button" onClick={() => { modalStore.showAbout = true; }} style={navLinkBtn} className="mmg-nav__link">About Us</button>
          <button type="button" onClick={() => { modalStore.showFAQ = true; }} style={navLinkBtn} className="mmg-nav__link">FAQ</button>
          <button type="button" onClick={() => navigate("/articles")} style={navLinkBtn} className="mmg-nav__link">Articles</button>
          <a href={PHONE_HREF} className="mmg-nav__phone" style={{ display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 13, color: C.ink, textDecoration: "none" }}>
            <Phone size={14} color={C.ink} />{PHONE}
          </a>
          {/* Right cluster. Logged out: Sign In (ghost) + Get a quote (solid).
              Logged in: Get a quote + the avatar/initials dropdown. */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {!isAuthenticated && (
              <button type="button" onClick={onSignIn} style={ghostBtn} className="mmg-nav__ghost"
                onMouseEnter={(e) => { e.currentTarget.style.background = C.accentTint; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                Sign In
              </button>
            )}
            <button type="button" onClick={onGetQuote} style={solidNavBtn} className="mmg-nav__cta"
              onMouseEnter={(e) => { e.currentTarget.style.background = C.accentStrong; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = C.accent; }}>
              Get a quote
            </button>
            {isAuthenticated && (
              <div ref={acctRef} style={{ position: "relative" }}>
                <button
                  type="button"
                  onClick={() => setAcctOpen((o) => !o)}
                  aria-label="Account menu"
                  aria-expanded={acctOpen}
                  style={{ display: "inline-flex", alignItems: "center", gap: 6, background: "none", border: 0, padding: 0, cursor: "pointer" }}
                >
                  <span style={avatarCircle}>{initials}</span>
                  <ChevronDown size={16} color={C.ink} style={{ transform: acctOpen ? "rotate(180deg)" : "none", transition: "transform .15s ease" }} />
                </button>
                {acctOpen && (
                  <div style={{ position: "absolute", top: "calc(100% + 10px)", right: 0, minWidth: 190, background: "#fff", border: `1px solid ${C.border}`, borderRadius: 12, boxShadow: "0 12px 30px rgba(42,44,69,.14)", padding: "6px 0", zIndex: 80 }}>
                    <button type="button" style={dropItem} onClick={() => go(() => navigate("/dashboard"))}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.bgSubtle; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      Dashboard
                    </button>
                    <button type="button" style={dropItem} onClick={() => go(() => navigate("/profile"))}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.bgSubtle; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      Profile Settings
                    </button>
                    <div style={{ borderTop: `1px solid ${C.borderSoft}`, margin: "6px 0" }} />
                    <button type="button" style={dropItem} onClick={handleSignOut}
                      onMouseEnter={(e) => { e.currentTarget.style.background = C.bgSubtle; }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            )}
            {/* Hamburger — hidden on desktop, shown < 720px via responsive.css. */}
            <button
              type="button"
              className="mmg-nav__burger"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              style={{ display: "none", flexDirection: "column", gap: 4, background: "none", border: 0, padding: 6, cursor: "pointer" }}
            >
              <span style={{ display: "block", width: 20, height: 2, borderRadius: 2, background: C.ink }} />
              <span style={{ display: "block", width: 20, height: 2, borderRadius: 2, background: C.ink }} />
              <span style={{ display: "block", width: 20, height: 2, borderRadius: 2, background: C.ink }} />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu sheet — floating rounded panel over a dark scrim, per
          the approved mock: wordmark + close, then big tappable rows. */}
      {menuOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1200, fontFamily: FONT }}>
          <div onClick={closeAll} style={{ position: "absolute", inset: 0, background: "rgba(42,44,69,0.5)" }} />
          <div style={{ position: "absolute", top: 12, left: 12, width: "min(360px, 85vw)", maxHeight: "calc(100vh - 24px)", overflowY: "auto", background: "#fff", borderRadius: 14, boxShadow: "0 18px 50px rgba(42,44,69,.28)" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 24px" }}>
              <button type="button" onClick={() => go(() => navigate("/"))} style={{ background: "none", border: 0, padding: 0, cursor: "pointer", fontFamily: "inherit", fontWeight: 800, fontSize: 21, color: C.ink, letterSpacing: "-.01em" }}>
                MedMal<span style={{ color: C.accent }}>Guard</span>
              </button>
              <button type="button" onClick={closeAll} aria-label="Close menu" style={{ background: "none", border: 0, padding: 4, cursor: "pointer", display: "inline-flex" }}>
                <X size={22} color={C.inkSoft} />
              </button>
            </div>
            {sheetItems.map((item) => (
              <button key={item.label} type="button" style={sheetRow} onClick={item.raw ? item.onClick : () => go(item.onClick)}>
                {item.label}
              </button>
            ))}
            <a href={PHONE_HREF} style={{ ...sheetRow, gap: 10, color: C.accentStrong, fontSize: 16 }}>
              <Phone size={16} color={C.accentStrong} />{PHONE}
            </a>
            {!isAuthenticated && (
              <div style={{ padding: "8px 24px 22px" }}>
                <button type="button" onClick={() => go(onGetQuote)} style={{ ...solidNavBtn, width: "100%", padding: "12px 18px", fontSize: 15 }}>
                  Get a quote
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}

// Standalone MedMalGuard header for the wizard routes (steps 2–10). Renders
// the same NavBar the landing page uses, with its own sign-in/dashboard
// wiring so FlowLayout can mount it without threading props. Replaces the
// teal HubHeader on those routes. Always in the React tree; FlowLayout
// decides where it shows (it's not gated to desktop, matching the landing
// nav which is responsive).
export function MedMalGuardHeader() {
  const navigate = useNavigate();
  const insuredProfile  = useStore(insuredProfileStore, (s) => s.insuredProfile);
  const isAuthenticated = Boolean(insuredProfile?.id || insuredProfile?.name || insuredProfile?.username);
  const onSignIn = () => {
    if (isAuthenticated) { navigate("/dashboard"); return; }
    navigate("/signin");
  };
  // Off the landing, the calculator lives on "/", so "Get a quote" routes
  // there (the landing's own handler scrolls to the calculator in place).
  // Reset the form fields first so the calculator mounts blank.
  const onGetQuote = () => {
    quoteFormStore.zip = "";
    quoteFormStore.effDate = "";
    navigate("/");
  };
  return <NavBar onSignIn={onSignIn} onGetQuote={onGetQuote} isAuthenticated={isAuthenticated} />;
}

function TrustCell({ eyebrow, value, caption }) {
  return (
    <div>
      <div style={{ fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 9 }}>{eyebrow}</div>
      <div style={{ fontWeight: 800, fontSize: 26, lineHeight: 1, color: C.ink, marginBottom: 6 }}>{value}</div>
      <div style={{ fontWeight: 500, fontSize: 13, lineHeight: 1.4, color: C.body }}>{caption}</div>
    </div>
  );
}

function HowCard({ n, title, body, accent }) {
  const chipBg = accent ? C.accentTint : C.successTint;
  const chipFg = accent ? C.accentStrong : C.successStrong;
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 14, padding: 26, background: C.bg }}>
      <div style={{ width: 40, height: 40, borderRadius: 10, background: chipBg, color: chipFg, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800, fontSize: 18, marginBottom: 16 }}>{n}</div>
      <div style={{ fontWeight: 700, fontSize: 17, color: C.ink, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 14, lineHeight: 1.6, color: C.body }}>{body}</div>
    </div>
  );
}

// One line of the expanded estimate breakdown: label (with an optional pill)
// on the left, dollar amount on the right. Kept in the landing's kit palette.
function EstimateRow({ label, value, badge }) {
  return (
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "6px 0" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, color: C.inkSoft }}>
        {label}
        {badge && (
          <span style={{ fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: ".04em", color: C.accentStrong, background: C.accentTint, borderRadius: 20, padding: "3px 8px", whiteSpace: "nowrap" }}>
            {badge}
          </span>
        )}
      </span>
      <span style={{ fontSize: 14, color: C.inkSoft, whiteSpace: "nowrap" }}>${Number(value || 0).toLocaleString()}</span>
    </div>
  );
}

// A tinted note row (info circle or location pin + blue text), matching the
// landing's accent palette. Used for the estimate disclaimer and the
// "coverage is portable" callout.
function InfoNote({ children, icon = "info" }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, background: C.accentTint, borderRadius: 12, padding: "13px 16px", marginBottom: 14 }}>
      <svg viewBox="0 0 24 24" fill="none" stroke={C.accentStrong} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: 17, height: 17, flexShrink: 0, marginTop: 1 }}>
        {icon === "pin" ? (
          <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></>
        ) : (
          <><circle cx="12" cy="12" r="10" /><path d="M12 16v-4" /><line x1="12" y1="8" x2="12.01" y2="8" /></>
        )}
      </svg>
      <span style={{ fontSize: 13, color: C.accentStrong, lineHeight: 1.55, fontWeight: 500 }}>{children}</span>
    </div>
  );
}

// One label/value row inside the Coverage snapshot card.
function SnapRow({ label, value }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, padding: "5px 0" }}>
      <span style={{ fontSize: 14, color: C.body }}>{label}</span>
      <span style={{ fontSize: 14, color: C.ink, fontWeight: 600, textAlign: "right" }}>{value}</span>
    </div>
  );
}

function CalcField({ label, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontWeight: 600, fontSize: 12, textTransform: "uppercase", letterSpacing: ".05em", color: C.body, marginBottom: 8 }}>{label}</label>
      {children}
    </div>
  );
}

function CalcInput({ value, onChange, placeholder, inputMode, maxLength, onKeyDown, onBlur }) {
  return (
    <input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      inputMode={inputMode}
      maxLength={maxLength}
      autoComplete="off"
      style={{
        width: "100%", boxSizing: "border-box", border: `1px solid ${C.inputBorder}`,
        borderRadius: 10, padding: "13px 15px", fontFamily: "inherit", fontWeight: 600,
        fontSize: 16, color: C.ink, background: "#fff", outline: "none",
        transition: "background .15s ease-in-out, box-shadow .15s ease-in-out",
      }}
      onFocus={(e) => { e.target.style.boxShadow = FOCUS_RING; e.target.style.background = C.accentTint; }}
      onBlur={(e) => { e.target.style.boxShadow = "none"; e.target.style.background = "#fff"; if (onBlur) onBlur(e); }}
    />
  );
}

// Effective-date input: a manual-entry MM/DD/YYYY text field with a calendar
// affordance on the right. Typing works exactly as before. The native date
// picker opens when the calendar area is tapped/clicked.
//
// Mobile-safe: rather than call showPicker() on a hidden input (unsupported
// on iOS Safari, flaky on Android), a REAL transparent <input type="date"> is
// overlaid on the calendar-icon zone. Tapping the icon is tapping the date
// input itself, so the OS-native picker opens reliably everywhere. The glyph
// is drawn behind the transparent overlay; on desktop we also call
// showPicker() on click as a nicety.
function CalcDateInput({ value, onChange, placeholder, onKeyDown, onBlur, min, max }) {
  const dateRef = useRef(null);
  const ICON_ZONE = 44;
  // Convert the current MM/DD/YYYY text to the ISO (yyyy-mm-dd) the native
  // date input needs, so the picker opens on the already-entered date.
  const iso = (() => {
    const m = fmtDate(value); // "" unless 8 digits present
    if (!m) return "";
    const [mm, dd, yyyy] = m.split("/");
    return `${yyyy}-${mm}-${dd}`;
  })();
  return (
    <div style={{ position: "relative" }}>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        inputMode="numeric"
        autoComplete="off"
        style={{
          width: "100%", boxSizing: "border-box", border: `1px solid ${C.inputBorder}`,
          borderRadius: 10, padding: "13px 44px 13px 15px", fontFamily: "inherit", fontWeight: 600,
          fontSize: 16, color: C.ink, background: "#fff", outline: "none",
          transition: "background .15s ease-in-out, box-shadow .15s ease-in-out",
        }}
        onFocus={(e) => { e.target.style.boxShadow = FOCUS_RING; e.target.style.background = C.accentTint; }}
        onBlur={(e) => { e.target.style.boxShadow = "none"; e.target.style.background = "#fff"; if (onBlur) onBlur(e); }}
      />
      {/* Calendar glyph — visual only, behind the transparent date overlay. */}
      <span aria-hidden="true" style={{ position: "absolute", top: "50%", right: 13, transform: "translateY(-50%)", display: "inline-flex", pointerEvents: "none" }}>
        <Calendar size={18} color={C.accentStrong} />
      </span>
      {/* Real native date input, transparent, overlaying only the icon zone.
          Tapping/clicking it opens the OS-native picker directly. */}
      <input
        ref={dateRef}
        type="date"
        value={iso}
        min={min}
        max={max}
        aria-label="Open date picker"
        onChange={(e) => onChange(toMdY(e.target.value))}
        onClick={() => { const el = dateRef.current; if (el && typeof el.showPicker === "function") { try { el.showPicker(); } catch { /* opened via native tap */ } } }}
        style={{
          position: "absolute", top: 0, right: 0, height: "100%", width: ICON_ZONE,
          margin: 0, padding: 0, border: 0, background: "transparent",
          color: "transparent", opacity: 0, cursor: "pointer",
          WebkitAppearance: "none", appearance: "none",
        }}
      />
    </div>
  );
}

// ── Inline style objects ─────────────────────────────────────────────────

// Header sits flush at the top (no border/shadow); on scroll it gains a
// hairline + soft drop shadow (see navScrolled) so the nav/content boundary
// reads clearly. Sticky + transition give the smooth modern feel.
const nav = {
  background: C.bg,
  position: "sticky",
  top: 0,
  zIndex: 50,
  borderBottom: "1px solid transparent",
  boxShadow: "0 0 0 rgba(42,43,69,0)",
  transition: "box-shadow .2s ease, border-color .2s ease",
};
const navScrolled = {
  borderBottom: "1px solid #E5E7EB",
  boxShadow: "0 4px 16px rgba(42,43,69,.06)",
};
const navInner = { maxWidth: 1200, margin: "0 auto", padding: "18px 40px", display: "flex", alignItems: "center", justifyContent: "space-between" };
const ghostBtn = { display: "inline-flex", alignItems: "center", border: `1px solid #cdd9f8`, color: C.accent, background: "transparent", fontWeight: 700, fontSize: 13, padding: "9px 16px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "background .15s ease-in-out" };
// Solid primary header CTA — same footprint as ghostBtn (1px transparent
// border keeps both buttons the same height) so the pair aligns cleanly.
const solidNavBtn = { display: "inline-flex", alignItems: "center", border: "1px solid transparent", color: "#fff", background: C.accent, fontWeight: 700, fontSize: 13, padding: "9px 16px", borderRadius: 8, cursor: "pointer", fontFamily: "inherit", transition: "background .15s ease-in-out" };

const chip = { display: "inline-flex", alignItems: "center", gap: 8, background: C.accentTint, color: C.accentStrong, fontWeight: 700, fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", padding: "8px 12px", borderRadius: 20 };
const h1 = { fontWeight: 800, fontSize: 52, lineHeight: 1.05, letterSpacing: "-.02em", color: C.ink, margin: "18px 0 0" };
const sub = { fontSize: 18, lineHeight: 1.6, color: C.body, maxWidth: 480, margin: "16px 0 0" };
const callout = { display: "flex", alignItems: "center", gap: 14, background: C.successTint, border: `1px solid ${C.successBorder}`, borderRadius: 12, padding: "14px 18px", marginTop: 24 };

const calcCard = { background: "#fff", border: `1px solid ${C.border}`, borderRadius: 16, boxShadow: "0 12px 40px rgba(42,43,69,.10)", overflow: "hidden" };
const calcHeader = { background: C.bgSubtle, borderBottom: `1px solid ${C.borderSoft}`, padding: "16px 22px", display: "flex", alignItems: "center", justifyContent: "space-between" };
const estimateBlock = { background: "#f4f7fe", border: `1px solid #e1e9fb`, borderRadius: 12, padding: 18, marginBottom: 16 };
const snapCard = { border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: 14 };
const snapTitle = { fontWeight: 800, fontSize: 15, color: C.ink, marginBottom: 8 };
const ctaBtn = (ok) => ({ width: "100%", background: C.accent, color: "#fff", fontWeight: 700, fontSize: 16, fontFamily: "inherit", padding: 16, borderRadius: 10, border: 0, cursor: ok ? "pointer" : "not-allowed", opacity: ok ? 1 : 0.5, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, transition: "background .15s ease-in-out" });

const reviewCard = { border: `1px solid ${C.border}`, borderRadius: 14, padding: "18px 20px", background: C.bgSubtle };

const needCard = { border: `1px solid ${C.border}`, borderRadius: 16, padding: "28px 32px" };
const needRow = { display: "flex", alignItems: "center", gap: 14, textDecoration: "none", padding: "14px 0" };
const needIconWrap = { display: "inline-flex", width: 40, height: 40, alignItems: "center", justifyContent: "center", borderRadius: 10, background: C.accentTint, flexShrink: 0 };
const needRowLabel = { display: "block", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: ".06em", color: C.muted, marginBottom: 3 };
const needRowValue = { display: "block", fontWeight: 700, fontSize: 16, color: C.accentStrong };

const footer = { fontFamily: FONT, background: C.dark, padding: "26px 40px", display: "flex", alignItems: "center", justifyContent: "center", gap: 20, flexWrap: "wrap" };
